<template>
  <div class="development-evaluation-page">
    <nav id="breadcrumbs" :aria-label="$t('general.page_path')" class="mb-3">
      <ul>
        <li>
          <router-link :to="{ name: 'company.dashboard' }">
            <i class="uil-home-alt"></i>
          </router-link>
        </li>
        <li>{{ $t('general.development_evaluation') }}</li>
      </ul>
    </nav>

    <div class="section-header mb-lg-2 border-0 uk-flex-middle">
      <div class="section-header-left">
        <h2 class="uk-heading-line text-left mb-1">
          <span>{{ $t('general.development_evaluation') }}</span>
        </h2>
        <p class="text-muted mb-0">
          {{ $t('general.development_evaluation_description') }}
        </p>
      </div>
      <div class="section-header-right">
        <excel-download-button class="d-none" :loading="false"
                               :tooltipText="$t('filters.export_excell')"
                               @download="exportUsers"
        />
        <span class="total-users-badge">
          <i class="uil-users-alt"></i>
          {{ $t('general.total_users_count', { count: total }) }}
        </span>
      </div>
    </div>

    <div class="card evaluation-card">
      <div class="card-header border-0 evaluation-toolbar">
        <div class="toolbar-grid">
          <div class="filter-field search-field">
            <label for="development-user-search">{{ $t('general.user') }}</label>
            <div class="development-user-search-control">
              <i class="uil-search"></i>
              <input id="development-user-search"
                     v-model="search"
                     :aria-label="$t('general.search_user')"
                     :placeholder="$t('general.search_user_placeholder')"
                     class="form-control"
                     type="search"
                     @input="scheduleSearch"
                     @keydown.enter.prevent="applyFilters"
              />
            </div>
          </div>

          <div class="filter-field">
            <label>{{ $t('general.manager') }}</label>
            <multiple-select-input v-model="selectedManager"
                                   :allowEmpty="false"
                                   :disabled="loading"
                                   :searchable="true"
                                   :showLabels="false"
                                   :values="managers"
                                   class="evaluation-select"
                                   @select="selectFilter('selectedManager', $event)"
            />
          </div>

          <div class="filter-field">
            <label>{{ $t('general.department') }}</label>
            <multiple-select-input v-model="selectedDepartment"
                                   :disabled="loading"
                                   :searchable="true"
                                   :showLabels="false"
                                   :values="departments"
                                   class="evaluation-select"
                                   @select="selectFilter('selectedDepartment', $event)"
            />
          </div>

          <div class="filter-field">
            <label>{{ $t('general.competency') }}</label>
            <multiple-select-input v-model="selectedCompetency"
                                   :disabled="loading"
                                   :searchable="true"
                                   :showLabels="false"
                                   :values="competencies"
                                   class="evaluation-select"
                                   @select="selectFilter('selectedCompetency', $event)"
            />
          </div>

          <div class="filter-field date-field">
            <label for="development-start-date">{{ $t('general.period_start_date') }}</label>
            <input id="development-start-date"
                   v-model="startDate"
                   :max="endDate || undefined"
                   class="form-control"
                   type="date"
                   @change="applyFilters"
            />
          </div>

          <div class="filter-field date-field">
            <label for="development-end-date">{{ $t('general.period_end_date') }}</label>
            <input id="development-end-date"
                   v-model="endDate"
                   :min="startDate || undefined"
                   class="form-control"
                   type="date"
                   @change="applyFilters"
            />
          </div>

          <div class="filter-field sort-field">
            <label>{{ $t('general.sorting') }}</label>
            <multiple-select-input v-model="selectedSort"
                                   :allowEmpty="false"
                                   :searchable="false"
                                   :showLabels="false"
                                   :values="sortOptions"
                                   class="evaluation-select"
                                   @select="selectFilter('selectedSort', $event)"
            />
          </div>

        </div>
      </div>

      <div v-if="errorMessage" class="uk-alert-danger m-3" role="alert" uk-alert>
        <p class="mb-0">{{ errorMessage }}</p>
      </div>

      <default-loading v-if="loading && !users.length"></default-loading>

      <template v-else>
        <div v-if="users.length" class="table-responsive">
          <table class="table align-items-center evaluation-table">
            <thead>
            <tr>
              <th>{{ $t('general.user') }}</th>
              <th>{{ $t('general.email') }}</th>
              <th>{{ $t('general.manager') }}</th>
              <th class="text-center">{{ $t('general.total_competencies') }}</th>
              <th :aria-label="$t('general.detail')"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="user in users"
                :key="user.id"
                class="user-row"
                role="link"
                tabindex="0"
                @click="openUser(user)"
                @keydown.enter.prevent="openUser(user)"
                @keydown.space.prevent="openUser(user)"
            >
              <td>
                <div class="user-cell">
                  <AvatarOrIcon :alt="user.full_name || $t('general.user')"
                                :size-px="42"
                                :src="user.avatar || ''"
                  />
                  <div>
                    <strong>{{ user.full_name || "—" }}</strong>
                    <small v-if="user.title_name" class="d-block text-muted">
                      {{ user.title_name }}
                    </small>
                  </div>
                </div>
              </td>
              <td>{{ user.email || "—" }}</td>
              <td>{{ managerName(user) }}</td>
              <td class="text-center">
                <span class="competency-count">
                  {{ Number(user.competencies_count) || 0 }}
                </span>
              </td>
              <td class="text-right">
                  <span class="detail-link">
                    {{ $t('general.detail') }} <i class="uil-angle-right-b"></i>
                  </span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="empty-state">
          <i class="uil-users-alt"></i>
          <h4>{{ $t('general.users_not_found') }}</h4>
          <p>{{ $t('general.users_not_found_hint') }}</p>
        </div>

        <pagination v-if="totalPages > 1"
                    :pagination="pagination"
                    class="mt-2 mb-3"
                    @changePage="changePage($event)"/>
      </template>
    </div>
  </div>
</template>

<script>
import ExcelDownloadButton from "@/view/components/buttons/ExcelDownloadButton.vue";
import DefaultLoading from "@/view/components/loading/DefaultLoading.vue";
import AvatarOrIcon from "@/view/components/icons/AvatarOrIcon.vue";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";
import Pagination from "@/assets/components/Pagination.vue";
import ApiService from "@/core/services/api.service";
import querystring from "querystring";

export default {
  name: "DevelopmentEvaluationIndex",
  components: {
    AvatarOrIcon,
    DefaultLoading,
    ExcelDownloadButton,
    MultipleSelectInput,
    Pagination,
  },
  data() {
    return {
      users: [],
      managers: [],
      departments: [],
      competencies: [],
      selectedManager: null,
      selectedDepartment: null,
      selectedCompetency: null,
      selectedSort: null,
      search: "",
      startDate: "",
      endDate: "",
      page: 1,
      perPage: 15,
      total: 0,
      totalPages: 1,
      loading: false,
      errorMessage: "",
      searchTimer: null,
      requestSequence: 0,
    };
  },
  computed: {
    allManagersOption() {
      return { id: null, name: this.$t("general.all") };
    },
    sortOptions() {
      return [
        { id: "desc", name: this.$t("general.sort_highest_competency") },
        { id: "asc", name: this.$t("general.sort_lowest_competency") },
      ];
    },
    pagination() {
      return {
        current_page: this.page,
        last_page: this.totalPages,
        per_page: this.perPage,
        total: this.total,
      };
    },
  },
  created() {
    this.selectedManager = this.allManagersOption;
    this.selectedSort = this.sortOptions[0];
    this.managers = [this.allManagersOption];
    this.loadUsers();
    this.loadDepartments();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.requestSequence += 1;
  },
  methods: {
    queryParams(page, perPage) {
      const params = {
        page,
        perPage,
        order_by: "competencies_count",
        sort: this.selectedSort?.id || "desc",
        search: this.search.trim(),
        manager_id: this.selectedManager?.id,
        department_id: this.selectedDepartment?.id,
        competency_id: this.selectedCompetency?.id,
        // API tarihleri Y.m.d biçiminde bekliyor (input değeri YYYY-MM-DD)
        start_date: this.startDate.replace(/-/g, "."),
        end_date: this.endDate.replace(/-/g, "."),
      };
      // Boş filtreleri query string'e ekleme
      Object.keys(params).forEach((key) => {
        if (params[key] == null || params[key] === "") delete params[key];
      });
      return params;
    },
    async fetchPage(page, perPage) {
      const response = await ApiService.get(
          `api/competencies-evaluation?${querystring.stringify(
              this.queryParams(page, perPage)
          )}`
      );
      return response?.data || {};
    },
    async loadUsers() {
      const requestId = ++this.requestSequence;
      this.loading = true;
      this.errorMessage = "";

      try {
        const payload = await this.fetchPage(this.page, this.perPage);
        if (requestId !== this.requestSequence) return;

        const meta = payload.meta || {};
        this.users = Array.isArray(payload.data) ? payload.data : [];
        this.total = Number(meta.total) || this.users.length;
        this.page = Number(meta.current_page) || this.page;
        this.totalPages = Math.max(1, Number(meta.last_page) || 1);

        // Yönetici ve yetkinlik seçenekleri response içinde döner
        const filters = payload.filters || {};
        if (Array.isArray(filters.managers)) {
          this.managers = [
            this.allManagersOption,
            ...filters.managers.map((manager) => ({
              id: manager.id,
              name: manager.full_name,
            })),
          ];
        }
        if (Array.isArray(filters.competencies)) {
          this.competencies = filters.competencies;
        }
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        this.users = [];
        this.total = 0;
        this.totalPages = 1;
        this.errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            this.$t("general.users_load_failed");
      } finally {
        if (requestId === this.requestSequence) this.loading = false;
      }
    },
    async loadDepartments() {
      try {
        const response = await ApiService.get("api/department?perPage=100");
        const rows = response?.data?.data || response?.data || [];
        this.departments = Array.isArray(rows)
            ? rows.filter((item) => item && item.id != null)
            : [];
      } catch (error) {
        // Departman filtresi opsiyonel; liste yüklemesini engelleme.
      }
    },
    exportUsers() {
      // TODO: Excel export API'si bağlanacak.
      alert(this.$t("general.excel_download_coming_soon"));
    },
    // "select" eventi v-model güncellemesinden önce tetiklendiği için
    // seçimi burada set edip öyle filtreliyoruz.
    selectFilter(field, option) {
      this[field] = option;
      this.applyFilters();
    },
    scheduleSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.applyFilters, 350);
    },
    applyFilters() {
      clearTimeout(this.searchTimer);
      this.page = 1;
      this.loadUsers();
    },
    changePage(page) {
      page = Number(page);
      if (!page || page < 1 || page > this.totalPages || page === this.page) return;
      this.page = page;
      this.loadUsers();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    openUser(user) {
      if (!user?.id) return;
      this.$router.push({
        name: "company.development-evaluation.user",
        params: { userId: String(user.id) },
      });
    },
    managerName(user) {
      return user?.parent?.full_name || "—";
    },
  },
};
</script>

<style scoped>
.development-evaluation-page {
  width: 100%;
}

.section-header {
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
}

.section-header-right {
  align-items: center;
  display: flex;
  gap: 8px;
}

.total-users-badge {
  align-items: center;
  background: #eef4ff;
  border: 1px solid #dbe8ff;
  border-radius: 8px;
  color: #315ca8;
  display: inline-flex;
  font-size: 14px;
  font-weight: 600;
  gap: 7px;
  padding: 9px 13px;
}

.evaluation-card {
  border: 1px solid #e7ebf1;
  border-radius: 12px;
  overflow: visible;
}

.evaluation-toolbar {
  background: #fff;
  border-radius: 12px 12px 0 0;
  padding: 20px;
}

.toolbar-grid {
  align-items: end;
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.filter-field {
  min-width: 0;
}

.filter-field label {
  color: #4a5568;
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 7px;
  text-transform: uppercase;
}

.development-user-search-control {
  display: block;
  height: 42px;
  width: 100%;
  position: relative;
}

.development-user-search-control i {
  color: #94a0af;
  left: 13px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}

.development-evaluation-page .evaluation-toolbar .development-user-search-control > input[type="search"] {
  box-sizing: border-box;
  display: block;
  opacity: 1;
  visibility: visible;
  width: 100%;
  height: 42px;
  min-height: 42px;
  padding: 0 12px 0 38px;
  margin: 0;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  outline: 0;
  background: #fff;
  box-shadow: none;
  font-size: 13px !important;
  font-weight: 400;
  line-height: 1.2;
  appearance: none;
  -webkit-appearance: none;
}

.development-evaluation-page .evaluation-toolbar .development-user-search-control > input[type="search"]:focus {
  border-color: #7c94c8;
  outline: 0;
  box-shadow: 0 0 0 3px rgba(63, 111, 182, 0.1);
}

.evaluation-select {
  width: 100%;
  margin: 0;
}

.evaluation-select :deep(.form-group) {
  margin: 0;
}

.evaluation-select :deep(.multiselect) {
  height: auto !important;
  min-height: 42px !important;
  max-height: none !important;
  overflow: visible !important;
}

.evaluation-select :deep(.multiselect--active) {
  position: relative;
  z-index: 30;
}

.evaluation-select :deep(.multiselect__content-wrapper) {
  z-index: 40;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  box-shadow: 0 10px 24px rgba(30, 41, 59, 0.12);
}

.evaluation-select :deep(.multiselect__tags) {
  box-sizing: border-box;
  height: 42px !important;
  min-height: 42px !important;
  max-height: 42px !important;
  padding: 8px 40px 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: #fff;
}

.evaluation-select :deep(.multiselect__select) {
  height: 40px;
}

.evaluation-select :deep(.multiselect__placeholder),
.evaluation-select :deep(.multiselect__single) {
  box-sizing: border-box !important;
  width: 100% !important;
  height: 24px !important;
  min-height: 24px !important;
  margin: 0 0 8px !important;
  padding: 0 !important;
  color: #4a5568 !important;
  font-size: 13px !important;
  font-weight: 400 !important;
  line-height: 24px !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

.evaluation-select :deep(.multiselect:not(.multiselect--active) .multiselect__input) {
  position: absolute !important;
  width: 0 !important;
  min-width: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  overflow: hidden !important;
}

.evaluation-select :deep(.multiselect--active .multiselect__input) {
  position: relative !important;
  width: 100% !important;
  height: 24px !important;
  min-height: 24px !important;
  margin: 0 0 8px !important;
  padding: 0 !important;
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
  font-size: 13px !important;
  line-height: 24px !important;
}

.evaluation-select :deep(.multiselect--active .multiselect__tags) {
  border-color: #7c94c8;
  box-shadow: 0 0 0 3px rgba(63, 111, 182, 0.1);
}

.evaluation-select :deep(.multiselect--disabled) {
  opacity: 0.7;
}

.development-evaluation-page .evaluation-toolbar .date-field > input[type="date"] {
  box-sizing: border-box;
  display: block;
  width: 100%;
  height: 42px;
  min-height: 42px;
  padding: 0 12px;
  margin: 0;
  color: #4a5568;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  box-shadow: none;
  font-size: 13px !important;
  font-weight: 400;
  line-height: 1.2;
}

.development-evaluation-page .evaluation-toolbar .date-field > input[type="date"]:focus {
  border-color: #7c94c8;
  outline: 0;
  box-shadow: 0 0 0 3px rgba(63, 111, 182, 0.1);
}

.evaluation-table {
  margin-bottom: 0;
}

.evaluation-table thead th {
  background: #f8fafc;
  border-top: 1px solid #edf0f4;
  color: #718096;
  font-size: 12px;
  letter-spacing: 0.02em;
  padding: 13px 18px;
  text-transform: uppercase;
}

.evaluation-table tbody td {
  border-color: #edf0f4;
  padding: 15px 18px;
  vertical-align: middle;
}

.user-row {
  cursor: pointer;
  transition: background-color 0.18s ease;
}

.user-row:hover,
.user-row:focus {
  background: #f8fbff;
  outline: none;
}

.user-cell {
  align-items: center;
  display: flex;
  gap: 12px;
  min-width: 210px;
}

.competency-count {
  background: #ecf9f1;
  border-radius: 999px;
  color: #208653;
  display: inline-flex;
  font-weight: 700;
  justify-content: center;
  min-width: 38px;
  padding: 5px 11px;
}

.detail-link {
  color: #3975d1;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.empty-state {
  color: #778295;
  padding: 56px 20px;
  text-align: center;
}

.empty-state i {
  color: #a9b6c7;
  font-size: 48px;
}

.empty-state h4 {
  color: #3d4756;
  margin: 12px 0 5px;
}

@media (max-width: 1200px) {
  .toolbar-grid {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }

}

@media (max-width: 768px) {
  .section-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .toolbar-grid {
    grid-template-columns: 1fr;
  }

  .evaluation-toolbar {
    padding: 15px;
  }

  .evaluation-table {
    min-width: 760px;
  }

}
</style>
